import type {
  ViewFolderId,
  ViewId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  ViewFolderId as ViewFolderIdSchema,
  ViewId as ViewIdSchema,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  FolderResponse,
  type ViewColumnsConfig,
  ViewResponse,
} from "@redotech/effect-api-shared/rpc/view-folder";
import type { SupportTicketSort } from "@redotech/effect-api-shared/schema/query-options";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Schema } from "effect";
import { supportTicketCountsQueryKey } from "./support-ticket-query-keys";
import {
  toCreateSupportTicketFolderInput,
  toCreateSupportTicketViewInput,
  toUpdateSupportTicketFolderInput,
  toUpdateSupportTicketViewInput,
} from "./support-ticket-view-folder-requests";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const supportTicketViewQueryKeys = { all: ["views"] as const };

export const supportTicketFolderQueryKeys = { all: ["folders"] as const };

export interface SupportTicketViewFolderApi {
  getViewsAndFolders(): Promise<{
    views: readonly ViewResponse[];
    folders: readonly FolderResponse[];
  }>;
  createView(payload: {
    id: ViewId;
    name: string;
    filterGroup: FilterGroup | null;
    folderId: ViewFolderId | null;
    columns: ViewColumnsConfig | null;
    sort: readonly SupportTicketSort[] | null;
  }): Promise<void>;
  updateView(
    viewId: ViewId,
    payload: {
      name: string;
      filterGroup: FilterGroup | null;
      folderId: ViewFolderId | null;
      columns: ViewColumnsConfig | null;
      sort: readonly SupportTicketSort[] | null;
    },
  ): Promise<void>;
  deleteView(viewId: ViewId): Promise<void>;
  createFolder(payload: {
    id: ViewFolderId;
    name: string;
    viewIds: string[];
    isPrivate: boolean;
    defaultViews: string[];
  }): Promise<void>;
  updateFolder(
    folderId: ViewFolderId,
    payload: { name: string; viewIds: string[]; defaultViews: string[] },
  ): Promise<void>;
  deleteFolder(folderId: ViewFolderId): Promise<void>;
  saveUserViewFolderOrder(
    items: { id: string; type: "view" | "folder" }[],
  ): Promise<void>;
}

export interface CreateSupportTicketViewFolderCollectionOptions {
  queryClient: QueryClient;
  api: SupportTicketViewFolderApi;
}

function invalidateSupportTicketCounts(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: supportTicketCountsQueryKey });
}

export function createViewsCollection({
  queryClient,
  api,
}: CreateSupportTicketViewFolderCollectionOptions) {
  return createCollection(
    queryCollectionOptions({
      queryKey: supportTicketViewQueryKeys.all,
      schema: Schema.standardSchemaV1(ViewResponse),
      queryFn: async (): Promise<ViewResponse[]> => {
        const result = await api.getViewsAndFolders();
        return [...result.views];
      },
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      meta: { showErrorToast: true },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const view = mutation.modified;
          await api.createView(toCreateSupportTicketViewInput(view));
        }
        invalidateSupportTicketCounts(queryClient);
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const view = mutation.modified;
          await api.updateView(ViewIdSchema.make(String(mutation.key)), {
            ...toUpdateSupportTicketViewInput(view),
          });
        }
        invalidateSupportTicketCounts(queryClient);
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          await api.deleteView(ViewIdSchema.make(String(mutation.key)));
        }
        invalidateSupportTicketCounts(queryClient);
      },
    }),
  );
}

export function createFoldersCollection({
  queryClient,
  api,
}: CreateSupportTicketViewFolderCollectionOptions) {
  return createCollection(
    queryCollectionOptions({
      queryKey: supportTicketFolderQueryKeys.all,
      schema: Schema.standardSchemaV1(FolderResponse),
      queryFn: async (): Promise<FolderResponse[]> => {
        const result = await api.getViewsAndFolders();
        return [...result.folders];
      },
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      meta: { showErrorToast: true },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const folder = mutation.modified;
          await api.createFolder(toCreateSupportTicketFolderInput(folder));
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const folder = mutation.modified;
          await api.updateFolder(
            ViewFolderIdSchema.make(String(mutation.key)),
            toUpdateSupportTicketFolderInput(folder),
          );
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          await api.deleteFolder(ViewFolderIdSchema.make(String(mutation.key)));
        }
      },
    }),
  );
}

export type ViewsCollection = ReturnType<typeof createViewsCollection>;
export type FoldersCollection = ReturnType<typeof createFoldersCollection>;
